import { useRef } from 'react';
import { Button } from './Button';
import { Dialog } from './Dialog';
import type { ConfirmOptions } from './dialog-store';
import type { DensityLevel } from './layout/Density';

/**
 * 🔴 A YES-OR-NO QUESTION — design-system plan DS-8 (5.1; F496, F72, F119, F137, F182, F596). What `window.confirm`
 * was, drawn by the kit: an `alertdialog` whose title is the verb and the object ("Void invoice INV-0042?"), whose
 * body is the consequence, and whose confirm button repeats the verb ("Void invoice"). Cancel is the other button.
 *
 * `tone="danger"` is for a verb that cannot be taken back. Its confirm button is THE ONE filled danger control in the
 * product (O-27: destructive buttons stay outlined everywhere else) — the danger colour's text on the raised surface
 * inside a 2px danger border, no fill token — and focus starts on the way back, so an Enter pressed out of habit
 * cancels. A default question starts on the verb.
 *
 * Most screens ask through `useConfirm()` and never render this; it is exported for a screen that holds the question
 * in its own state (and wants `busy` while the verb runs).
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  busy,
  density,
  onConfirm,
  onCancel,
}: ConfirmOptions & { busy?: boolean; density?: DensityLevel; onConfirm: () => void; onCancel: () => void }) {
  const back = useRef<HTMLButtonElement>(null);
  const verb = useRef<HTMLButtonElement>(null);
  const danger = tone === 'danger';
  return (
    <Dialog
      role="alertdialog"
      density={density}
      title={title}
      body={body}
      onCancel={onCancel}
      initialFocus={danger ? back : verb}
      actions={
        <>
          <Button ref={back} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={verb}
            variant={danger ? 'danger' : 'primary'}
            className={danger ? 'wb-dialog__confirm--danger' : undefined}
            busy={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
